import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import SEO from "../../components/SEO";
import ReviewStars from "../../components/limelite/marketplace/ReviewStars";
import ReviewsPanel from "../../components/limelite/marketplace/ReviewsPanel";
import DomainStatusBadge from "../../components/limelite/marketplace/DomainStatusBadge";

const CATEGORY_COLORS = {
  ai:              "text-purple-400 bg-purple-900/20 border-purple-800/40",
  saas:            "text-blue-400 bg-blue-900/20 border-blue-800/40",
  "developer-tool":"text-lime-400 bg-lime-900/20 border-lime-800/40",
  productivity:    "text-amber-400 bg-amber-900/20 border-amber-800/40",
  ecommerce:       "text-pink-400 bg-pink-900/20 border-pink-800/40",
  mobile:          "text-cyan-400 bg-cyan-900/20 border-cyan-800/40",
  other:           "text-silver-dark bg-gray-900/20 border-gray-800/40",
};

const CATEGORY_LABELS = {
  ai: "AI", saas: "SaaS", "developer-tool": "Dev Tool",
  productivity: "Productivity", ecommerce: "eCommerce", mobile: "Mobile", other: "Other",
};

function AppIcon({ iconUrl, name }) {
  if (iconUrl) {
    return <img src={iconUrl} alt={name} className="w-20 h-20 rounded-2xl object-cover flex-shrink-0 border border-gray-800" />;
  }
  return (
    <div className="w-20 h-20 rounded-2xl bg-gray-800 border border-gray-700 flex items-center justify-center text-3xl font-bold text-silver-dark flex-shrink-0">
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );
}

export default function LimeliteMarketplaceDetailPage() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenshotIdx, setScreenshotIdx] = useState(0);
  const [reviewData, setReviewData] = useState({ reviews: [], summary: { average: 0, count: 0 } });

  useEffect(() => {
    setLoading(true);
    Promise.all([axios.get(`/api/marketplace/listings/${encodeURIComponent(slug)}`), axios.get(`/api/marketplace/listings/${encodeURIComponent(slug)}/reviews`)])
      .then(([listingResponse, reviewsResponse]) => {
        setListing(listingResponse.data.listing);
        setReviewData({ reviews: reviewsResponse.data.reviews || [], summary: reviewsResponse.data.summary || { average: 0, count: 0 } });
      })
      .catch(() => setListing(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-lime-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-silver-dark text-sm">App not found or no longer listed.</p>
          <Link to="/limelite/marketplace" className="text-lime-500 hover:text-lime-400 text-sm underline">
            ← Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  const catColor = CATEGORY_COLORS[listing.category] || CATEGORY_COLORS.other;
  const catLabel = CATEGORY_LABELS[listing.category] || listing.category;
  const isAndroid = listing.platform === "android" || listing.platform === "cross-platform";
  const shots = listing.screenshotUrls || [];

  return (
    <>
      <SEO
        title={`${listing.name} — Limelite Marketplace`}
        description={listing.tagline}
        url={`/limelite/marketplace/${listing.slug}`}
      />
      <div className="bg-black text-silver-light min-h-[calc(100vh-4rem)] px-4 py-10">
        <div className="max-w-3xl mx-auto space-y-8">

          {/* Back */}
          <Link
            to="/limelite/marketplace"
            className="inline-flex items-center gap-1.5 text-silver-dark hover:text-silver-light text-sm transition-colors group"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-0.5 transition-transform" aria-hidden="true">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Marketplace
          </Link>

          {/* Header */}
          <div className="flex items-start gap-5">
            <AppIcon iconUrl={listing.iconUrl} name={listing.name} />
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-medium">{listing.name}</h1>
                {listing.featured && (
                  <span className="text-xs text-amber-300 border border-amber-700/60 bg-amber-900/20 rounded px-2 py-0.5">Featured</span>
                )}
              </div>
              <p className="text-silver-dark">{listing.tagline}</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-xs border rounded px-2 py-0.5 ${catColor}`}>{catLabel}</span>
                {listing.platform !== "web" && (
                  <span className="text-xs text-silver-dark border border-gray-700 rounded px-2 py-0.5 capitalize">{listing.platform}</span>
                )}
                {listing.tags?.map(t => (
                  <span key={t} className="text-xs text-silver-dark">#{t}</span>
                ))}
                <ReviewStars average={listing.ratingAverage} count={listing.ratingCount} />
                <DomainStatusBadge listing={listing} />
              </div>
            </div>
          </div>

          {/* CTA buttons */}
          <div className="flex gap-3 flex-wrap">
            {listing.appUrl && (
              <a
                href={listing.appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-lime-600 hover:bg-lime-500 text-black font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors"
              >
                Visit app
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            )}
            {isAndroid && listing.apkUrl && (
              <a
                href={listing.apkUrl}
                className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 border border-gray-700 text-silver-medium px-6 py-2.5 rounded-xl text-sm transition-colors"
              >
                Download APK
              </a>
            )}
          </div>

          {/* Screenshots */}
          {shots.length > 0 && (
            <div className="space-y-3">
              <img
                src={shots[screenshotIdx]}
                alt={`Screenshot ${screenshotIdx + 1}`}
                className="w-full rounded-xl border border-gray-800 object-cover max-h-96"
              />
              {shots.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {shots.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setScreenshotIdx(i)}
                      className={`flex-shrink-0 w-16 h-10 rounded border overflow-hidden transition-colors ${i === screenshotIdx ? "border-lime-600" : "border-gray-700 hover:border-gray-500"}`}
                    >
                      <img src={s} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {listing.description && (
            <section className="bg-off-black border border-gray-800 rounded-xl p-5">
              <h2 className="text-sm font-medium text-silver-dark uppercase tracking-wider mb-3">About</h2>
              <p className="text-silver-medium text-sm leading-relaxed whitespace-pre-wrap">{listing.description}</p>
            </section>
          )}

          {/* Tech stack */}
          {listing.techStack?.length > 0 && (
            <section className="bg-off-black border border-gray-800 rounded-xl p-5">
              <h2 className="text-sm font-medium text-silver-dark uppercase tracking-wider mb-3">Tech stack</h2>
              <div className="flex flex-wrap gap-2">
                {listing.techStack.map(t => (
                  <span key={t} className="text-xs text-silver-medium border border-gray-700 rounded px-2.5 py-1">{t}</span>
                ))}
              </div>
            </section>
          )}

          <ReviewsPanel
            key={listing.slug}
            slug={listing.slug}
            initialReviews={reviewData.reviews}
            initialSummary={reviewData.summary}
            onReviewSaved={(summary) => setListing((current) => ({ ...current, ratingAverage: summary.average, ratingCount: summary.count }))}
          />

          {/* Footer meta */}
          <div className="flex items-center justify-between text-xs text-silver-dark border-t border-gray-800 pt-4">
            <span>
              {listing.viewCount != null ? `${listing.viewCount} view${listing.viewCount !== 1 ? "s" : ""}` : ""}
            </span>
            <span>
              Listed {listing.createdAt ? new Date(listing.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
            </span>
          </div>

          {/* Deploy CTA */}
          <div className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 text-center">
            <p className="text-silver-dark text-sm mb-3">Built with Limelite Deploy</p>
            <Link
              to="/limelite/deploy"
              className="inline-flex items-center bg-lime-600 hover:bg-lime-500 text-black font-semibold py-2 px-5 rounded-xl text-sm transition-colors"
            >
              Deploy your own app →
            </Link>
          </div>

        </div>
      </div>
    </>
  );
}
